import { SearchOutlined, FilterListOutlined } from '@mui/icons-material';
import { CardContainer, DataTable, Tag, SearchBar, Dropdown } from 'finara-components';
import type { DataTableColumn } from 'finara-components';
import React, { useState, useMemo } from 'react';

import { CsvExportButton } from '@/components/common';
import { ErrorState } from '@/components/common/ErrorState';
import { LoadingState } from '@/components/common/LoadingState';
import { useClientTransactions } from '@/hooks/queries/useSuperAdminClients';
import type { Transaction } from '@/types/employeeProfile';
import type { ClientTransactionsQuery } from '@/types/superAdminClients';

interface MovimientosTabProps {
  clientId: string;
}

export const MovimientosTab: React.FC<MovimientosTabProps> = ({ clientId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<
    'credit' | 'debit' | 'transfer' | 'payroll' | 'stp' | ''
  >('');
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | ''>('');
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  // Build query
  const query: ClientTransactionsQuery = useMemo(() => {
    const q: ClientTransactionsQuery = {
      organization_id: clientId,
      page: currentPage,
      limit,
      orderDirection: 'DESC',
    };

    if (typeFilter) q.type = typeFilter;
    if (statusFilter) q.status = statusFilter;

    return q;
  }, [clientId, currentPage, typeFilter, statusFilter]);

  const { data, isLoading, isError, refetch } = useClientTransactions(query);

  // Calculate total pages from pagination data
  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  // Format currency
  const formatCurrency = (value: number) => {
    return value.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Map transaction type to Spanish label
  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      credit: 'Crédito',
      debit: 'Débito',
      transfer: 'Transferencia',
      payroll: 'Nómina',
      stp: 'SPEI',
    };
    return typeMap[type] || type;
  };

  // Map status to Spanish label
  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: 'Pendiente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
    };
    return statusMap[status] || status;
  };

  // Get status tag variant
  const getStatusVariant = (status: string): 'green' | 'yellow' | 'red' | 'white' => {
    switch (status) {
      case 'approved':
        return 'green';
      case 'pending':
      case 'processing':
        return 'yellow';
      case 'rejected':
        return 'red';
      default:
        return 'white';
    }
  };

  // Filter transactions by search term (client-side)
  const filteredTransactions = useMemo(() => {
    if (!data?.data) return [];

    if (!searchTerm) return data.data;

    return data.data.filter(tx => tx.description.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [data?.data, searchTerm]);

  const transactionColumns: DataTableColumn<Transaction>[] = [
    {
      key: 'date',
      label: 'Fecha',
      render: (_, transaction) => (
        <span className="text-sm text-brand-white">{formatDate(transaction.createdAt)}</span>
      ),
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, transaction) => <Tag variant="blue">{getTypeLabel(transaction.type)}</Tag>,
    },
    {
      key: 'side',
      label: 'Dirección',
      render: (_, transaction) => (
        <Tag variant={transaction.side === 'in' ? 'green' : 'white'}>
          {transaction.side === 'in' ? 'Entrada' : 'Salida'}
        </Tag>
      ),
    },
    {
      key: 'concept',
      label: 'Concepto',
      render: (_, transaction) => (
        <span className="text-sm text-brand-white">{transaction.description || 'N/A'}</span>
      ),
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (_, transaction) => (
        <span className="text-sm font-semibold text-brand-white">
          ${formatCurrency(transaction.amount)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estatus',
      render: (_, transaction) => (
        <Tag variant={getStatusVariant(transaction.status)}>
          {getStatusLabel(transaction.status)}
        </Tag>
      ),
    },
  ];

  return (
    <CardContainer>
      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col lg:!flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search" className="block text-sm font-medium text-muted mb-2">
              <SearchOutlined fontSize="small" className="inline mr-1" />
              Buscar por concepto...
            </label>
            <SearchBar
              id="search"
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Buscar por concepto..."
              className="w-full"
            />
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label htmlFor="type-filter" className="block text-sm font-medium text-muted mb-2">
                <FilterListOutlined fontSize="small" className="inline mr-1" />
                Todos los tipos
              </label>
              <Dropdown
                id="type-filter"
                value={typeFilter}
                onChange={value => setTypeFilter(String(value) as typeof typeFilter)}
                options={[
                  { value: '', label: 'Todos los tipos' },
                  { value: 'credit', label: 'Crédito' },
                  { value: 'debit', label: 'Débito' },
                  { value: 'transfer', label: 'Transferencia' },
                  { value: 'payroll', label: 'Nómina' },
                  { value: 'stp', label: 'SPEI' },
                ]}
                placeholder="Todos los tipos"
                className="w-full lg:!w-48"
              />
            </div>

            <div className="flex-1">
              <label
                htmlFor="status-filter"
                className="block text-sm font-medium text-muted mb-2"
              >
                Todos los estados
              </label>
              <Dropdown
                id="status-filter"
                value={statusFilter}
                onChange={value => setStatusFilter(String(value) as typeof statusFilter)}
                options={[
                  { value: '', label: 'Todos' },
                  { value: 'pending', label: 'Pendiente' },
                  { value: 'approved', label: 'Aprobado' },
                  { value: 'rejected', label: 'Rechazado' },
                ]}
                placeholder="Todos"
                className="w-full lg:!w-48"
              />
            </div>
          </div>

          <div className="flex items-end">
            <CsvExportButton
              data={filteredTransactions}
              filename="movimientos_cliente"
              columns={tx => ({
                Fecha: formatDate(tx.createdAt),
                Tipo: getTypeLabel(tx.type),
                Dirección: tx.side === 'in' ? 'Entrada' : 'Salida',
                Concepto: tx.description || 'N/A',
                Monto: `$${formatCurrency(tx.amount)}`,
                Estatus: getStatusLabel(tx.status),
              })}
              variant="white"
              size="small"
              buttonText="Exportar"
              className="w-full lg:!w-auto"
            />
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div>
        <h3 className="text-base font-semibold text-brand-white mb-4">
          Historial Financiero {!isLoading && data?.total ? `(${data.total})` : ''}
        </h3>

        {isLoading ? (
          <LoadingState message="Cargando transacciones..." fullHeight={false} />
        ) : isError ? (
          <ErrorState
            title="Error al cargar las transacciones"
            message="No se pudieron cargar las transacciones. Por favor, intenta nuevamente."
            onRetry={() => void refetch()}
            useCard={false}
          />
        ) : filteredTransactions.length === 0 ? (
          <p className="text-center text-muted py-8">No se encontraron transacciones</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <DataTable columns={transactionColumns} data={filteredTransactions} />
            </div>

            {/* Pagination */}
            {data && totalPages > 1 && (
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-border rounded text-brand-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/5"
                >
                  Anterior
                </button>
                <span className="text-sm text-muted">
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-border rounded text-brand-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/5"
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </CardContainer>
  );
};
